import Vue from 'vue';
import {HTTP} from '@/utils/httpCommonUtil';
import {BallPulseLoader} from 'vue-loaders';
import VueSimpleSpinner from 'vue-simple-spinner';
import flatPickr from 'vue-flatpickr-component';
import {UserRoleEnum} from '@/enums/userRolesEnum';
import GenericModal from '@/componentsReusable/genericModal.vue';
import UploadControl from '@/componentsReusable/UploadControl.vue';

export default Vue.component('facility-documents-card', {
  name: 'FacilityDocumentsCard',
  data () {
    return {
      cardExpanded: false,
      loadingInitialData: false,
      savingNewFacilityDocument: false,
      facilityDocuments: [] as any[],
      isAuthorizedToDelete: false,
      showConfirmationModal: false,
      pendingDeleteId: null,
      facilityDocument: { "facilityId" : this.$route.params.facilityId, "isEditing" : true, "isNew" : false }
    }
  },
  components: {
    flatPickr,
    BallPulseLoader,
    VueSimpleSpinner,
    UploadControl,
    GenericModal
  },
  computed: {
    isFacilityUser(): boolean {
      return this.$store.state.user.isFacility;
    },
  },
  beforeMount: function () {
    this.isAuthorizedToDelete = this.$store.getters['user/hasRole']([UserRoleEnum.MANAGER]);
  },
  methods: {
    async expandCard () {
      this.cardExpanded = !this.cardExpanded;
      let iFacilityId = this.$route.params.facilityId;
      let facilityDocument: any = { facilityId: iFacilityId };
      facilityDocument.isEditing = true;
      facilityDocument.isNew = false;
      if (this.cardExpanded) {
        await this.getFacilityDocuments()
      }
    },
    async getFacilityDocuments () {
      let showLoadingSpinnerTimeout = setTimeout(() => { this.loadingInitialData = true; }, 0);
      const iFacilityId = this.$route.params.facilityId;
      try {
        const response = await HTTP.get('/api/facilities/' + iFacilityId + '/facilityDocuments');
        clearTimeout(showLoadingSpinnerTimeout);
        this.loadingInitialData = false;
        if (!response.data || !response.data.data) {
          throw new Error("Error retrieving Facility Documents.");
        }
        let freshFacilityDocuments = response.data.data;
        freshFacilityDocuments.forEach( (facilityDocument: any) => {
          facilityDocument.isEditing = true;
          facilityDocument.isNew = false;
          return false;
        });

        this.facilityDocuments = freshFacilityDocuments;
      } catch (err) {
        clearTimeout(showLoadingSpinnerTimeout);
        this.loadingInitialData = false;
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An error occurred retrieving Facility Documents.'
        });
      }
    },
    addFacilityDocument () {
      // Collapse any editing Facility Documents
      // Delete any existing "new" Facility Documents
      let indexesToDelete: any = [];
      this.facilityDocuments.forEach(function (facilitydoc, index) {
        if (facilitydoc.isNew) {
          indexesToDelete.push(index);
        }
        facilitydoc.isEditing = false;
      });
      let self = this;
      indexesToDelete.forEach(function (indx: number) {
        self.facilityDocuments.splice(indx,1);
      });

      this.facilityDocuments.unshift(
        {
          "facilityId": this.$route.params.facilityId,
          "isNew": true,
          "isEditing": true
        }
      );
    },
    async associateUploadedFile (s3FilePath: string, facilityId: any, fileName: string, facilityDocument: any) {
      let self = this;
      const entityType = 11;
      let iFacilityId = this.$route.params.facilityId;
      HTTP.post('/api/attachment/', {
        "entityId": iFacilityId,
        "entityType": entityType,
        "attachmentName": fileName,
        "s3Key": s3FilePath
      })
      .then(async (response: any) => {
        if (!response.data || !response.data.success) {
          throw new Error("Error associating uploaded document.");
        }
        await self.getFacilityDocuments();
        self.$notify({
          group: 'main',
          type: 'success',
          title: 'Successful Document Upload',
          text: 'Document ' + fileName + ' has been successfully uploaded.'
        });
      })
      .catch(function (err: any) {
        console.error(err);
        self.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An error occurred saving uploaded document.'
        });
      })
      await this.getFacilityDocuments();
    },
    openFacilityDocument (s3Key: string) {
      // get pre-signed url
      // open pre-signed url in a new tab
      let self = this;
      const url = this.isFacilityUser ? '/api/s3/signed-url/get-facility' : '/api/s3/signed-url/get';
      HTTP.get(url + `?s3key=${s3Key}`)
      .then(function (response) {
        if (!response.data || !response.data.data) {
          throw new Error("Error getting attachment.");
        }
        window.open(response.data.data, '_blank');
      })
      .catch(function (err: any) {
        console.error(err);
        self.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An error occurred saving uploaded document.'
        });
      })
    },
    async deleteConfirmed (id: any) {
      this.showConfirmationModal = false;
      await this.deleteFacilityDocument(this.pendingDeleteId, true);
    },
    async deleteFacilityDocument (id: any, bypassDeleteConfirmation: boolean = false ) {
      if (!bypassDeleteConfirmation) {
        // Show Delete Confirmation Modal
        this.showConfirmationModal = true;
        this.pendingDeleteId = id;
        return;
      } else {
        this.pendingDeleteId = null;
      }

      try {
        await HTTP.post('/api/facilityDocument/' + id);
        await this.getFacilityDocuments();
      } catch (err) {
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An error occurred deleting document'
        });
      }
    },
    async update(facilityDocument: any) {
      try {
        await HTTP.put(`/api/attachments/${facilityDocument.id}`, { exposedToFacilities: !facilityDocument.exposedToFacilities});
        // If it succeeds, update the model. If it fails, nothing has to happen because it will throw into the catch block but we didn't modify it before calling the server.
        facilityDocument.exposedToFacilities = !facilityDocument.exposedToFacilities;
      } catch (err) {
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An error occurred updating "Expose to Facility" switch.'
        })
      }
    }
  }
})



// WEBPACK FOOTER //
// ./src/components/facility/cards/documents/FacilityDocumentsCard.ts